import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { moduleConfig, ModuleConfigItem } from './moduleConfig';

interface ModuleToggleProps {
  onModuleToggle: (moduleId: string, visible: boolean) => void;
  visibleModules: Record<string, boolean>;
  isReportRendered: boolean;
  selectedLanguage?: string;
  onLanguageChange?: (language: string) => void;
  onPriceTypeChange?: (priceType: 'original' | 'closing') => void;
  excludeRent?: boolean;
  excludeSale?: boolean;
  onExcludeChange?: (excludeRent: boolean, excludeSale: boolean) => void;
  operation?: number;
  reportType?: number;
}

const ToggleContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 80px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-width: 300px;
  max-height: 500px;
  overflow-y: auto;
`;

const ToggleHeader = styled.h3`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const ModuleItem = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  padding: 8px;
  border-radius: 4px;
  background: ${props => props.disabled ? '#e9ecef' : '#f8f9fa'};
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

const ModuleItemHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const ModuleLabel = styled.label<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 12px;
  color: ${props => props.disabled ? '#6c757d' : '#555'};
  flex: 1;
`;

const Checkbox = styled.input<{ disabled?: boolean }>`
  margin-right: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const ModuleLink = styled.a<{ disabled?: boolean }>`
  margin-left: 8px;
  color: ${props => props.disabled ? '#6c757d' : '#007bff'};
  text-decoration: none;
  font-size: 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  display: flex;
  align-items: center;
  
  &:hover {
    text-decoration: ${props => props.disabled ? 'none' : 'underline'};
  }
`;

const PriceSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  color: #555;
  background: white;
  cursor: pointer;
  margin-bottom: 12px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
  
  &:hover {
    border-color: #007bff;
  }
`;

const PriceDescription = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
`;

const PriceDescriptionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
`;

const PriceDescriptionText = styled.div`
  font-size: 11px;
  color: #666;
  line-height: 1.5;
`;

const ToggleButton = styled.button`
  position: fixed;
  top: 20px;
  left: 20px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #0056b3;
  }
`;

const LanguageToggleButton = styled.button`
  position: fixed;
  top: 80px;
  left: 20px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #0056b3;
  }
`;

const LanguageSelectorContainer = styled.div`
  position: fixed;
  top: 80px;
  left: 80px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
`;

const LanguageSelectorLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #333;
`;

const LanguageSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  color: #555;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
  
  &:hover {
    border-color: #007bff;
  }
`;

const ExcludeCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px;
  border-radius: 4px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  position: relative;
  z-index: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const ExcludeCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 12px;
  color: #212529;
  flex: 1;
  font-weight: 500;
  position: relative;
  z-index: 1;
`;

const ExcludeCheckbox = styled.input`
  margin-right: 8px;
  cursor: pointer;
  width: 16px;
  height: 16px;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-left: 4px;
  color: #007bff;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #0056b3;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ExcludeOptionsContainer = styled.div`
  width: 100%;
  position: relative;
`;

const ExcludeOptionsLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

export const ModuleToggle: React.FC<ModuleToggleProps> = ({
  onModuleToggle,
  visibleModules,
  isReportRendered,
  selectedLanguage = 'es',
  onLanguageChange,
  onPriceTypeChange,
  excludeRent = false,
  excludeSale = false,
  onExcludeChange,
  operation,
  reportType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);
  const [isValuationExpanded, setIsValuationExpanded] = useState(false);
  const [isComparablesExpanded, setIsComparablesExpanded] = useState(false);
  const [selectedPriceType, setSelectedPriceType] = useState<'original' | 'closing'>('original');
  const [localExcludeRent, setLocalExcludeRent] = useState(excludeRent ?? false);
  const [localExcludeSale, setLocalExcludeSale] = useState(excludeSale ?? false);
  const intl = useIntl();
  const toggleContainerRef = useRef<HTMLDivElement>(null);
  const languageContainerRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const languageButtonRef = useRef<HTMLButtonElement>(null);

  // Función para manejar el toggle de módulos (cierra los demás)
  const handleModulesToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState) {
      setIsLanguageSelectorOpen(false);
    }
  };

  // Función para manejar el toggle de idioma (cierra los demás)
  const handleLanguageToggle = () => {
    const newState = !isLanguageSelectorOpen;
    setIsLanguageSelectorOpen(newState);
    if (newState) {
      setIsOpen(false);
    }
  };

  // Función para manejar el toggle de expansión del módulo valuation
  const handleValuationExpand = () => {
    setIsValuationExpanded(!isValuationExpanded);
  };

  // Función para manejar el toggle de expansión del módulo comparables
  const handleComparablesExpand = () => {
    setIsComparablesExpanded(!isComparablesExpanded);
  };

  // Función para manejar el cambio de idioma
  const handleLanguageChange = (language: string) => {
    if (onLanguageChange) {
      onLanguageChange(language);
    }
  };

  // Función para manejar el cambio de exclude_rent
  const handleExcludeRentChange = (checked: boolean) => {
    setLocalExcludeRent(checked);
    if (onExcludeChange) {
      onExcludeChange(checked, localExcludeSale);
    }
  };

  // Función para manejar el cambio de exclude_sale
  const handleExcludeSaleChange = (checked: boolean) => {
    setLocalExcludeSale(checked);
    if (onExcludeChange) {
      onExcludeChange(localExcludeRent, checked);
    }
  };

  // Sincronizar valores locales cuando cambian las props (valores de getUserReportData)
  useEffect(() => {
    if (excludeRent !== undefined) {
      setLocalExcludeRent(excludeRent);
    }
    if (excludeSale !== undefined) {
      setLocalExcludeSale(excludeSale);
    }
  }, [excludeRent, excludeSale]);

  // Cerrar desplegables al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Verificar si el clic fue fuera del contenedor de módulos y su botón
      if (
        isOpen &&
        toggleContainerRef.current &&
        toggleButtonRef.current &&
        !toggleContainerRef.current.contains(target) &&
        !toggleButtonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
      
      // Verificar si el clic fue fuera del contenedor de idioma y su botón
      if (
        isLanguageSelectorOpen &&
        languageContainerRef.current &&
        languageButtonRef.current &&
        !languageContainerRef.current.contains(target) &&
        !languageButtonRef.current.contains(target)
      ) {
        setIsLanguageSelectorOpen(false);
      }
    };

    // Añadir el event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Limpiar el event listener al desmontar
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isLanguageSelectorOpen]);

  const handleToggle = (moduleId: string) => {
    const module = moduleConfig.find(m => m.id === moduleId);
    if (module?.disabled) {
      return; // No permitir cambios en módulos deshabilitados
    }
    const newVisible = !visibleModules[moduleId];
    onModuleToggle(moduleId, newVisible);
  };

  // Función para obtener la traducción del módulo
  const getModuleLabel = (moduleId: string): string => {
    const translationKey = `modules.${moduleId}`;
    return intl.formatMessage({ id: translationKey });
  };

  // Función para navegar a la sección del módulo
  const handleNavigateToModule = (anchor: string | undefined, moduleId: string) => {
    if (!anchor) return;
    
    // Buscar el elemento por ID o data-attribute
    const element = document.getElementById(anchor) || 
                    document.querySelector(`[data-module-id="${moduleId}"]`);
    
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Solo mostrar el botón si el informe está renderizado
  if (!isReportRendered) {
    return null;
  }

  return (
    <>
      <ToggleButton ref={toggleButtonRef} onClick={handleModulesToggle}>
        {isOpen ? '×' : '⚙️'}
      </ToggleButton>
      
      <LanguageToggleButton ref={languageButtonRef} onClick={handleLanguageToggle}>
        {isLanguageSelectorOpen ? '×' : '🌐'}
      </LanguageToggleButton>
      
      {isOpen && (
        <ToggleContainer ref={toggleContainerRef}>
          <ToggleHeader>
            {intl.formatMessage({ id: 'modules.configureModules' })}
          </ToggleHeader>
          {moduleConfig
            .filter(module => module.visible)
            .map((module) => (
              <React.Fragment key={module.id}>
                <ModuleItem disabled={module.disabled}>
                  <ModuleItemHeader>
                    <ModuleLabel disabled={module.disabled} style={{ flex: 1 }}>
                      {module.id !== 'valuation' && (
                        <Checkbox
                          type="checkbox"
                          checked={visibleModules[module.id] ?? true}
                          onChange={() => handleToggle(module.id)}
                          disabled={module.disabled}
                        />
                      )}
                      {getModuleLabel(module.id)}
                    </ModuleLabel>
                    {module.anchor && (
                      <ModuleLink
                        disabled={module.disabled}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                          e.preventDefault();
                          handleNavigateToModule(module.anchor, module.id);
                        }}
                        href={`#${module.anchor}`}
                        title={intl.formatMessage({ id: 'modules.navigateToModule' }, { module: getModuleLabel(module.id) })}
                      >
                        🔗
                      </ModuleLink>
                      
                    )}
                    {module.id === 'valuation' && operation !== undefined && reportType !== undefined && reportType !== 2 && (
                      <ExpandButton
                        onClick={handleValuationExpand}
                        disabled={false}
                        title={intl.formatMessage({ id: 'modules.excludeOptions' })}
                      >
                        {isValuationExpanded ? '▼' : '▶'}
                      </ExpandButton>
                    )}
                    {module.id === 'comparables' && operation === 1 && (
                      <ExpandButton
                        onClick={handleComparablesExpand}
                        disabled={false}
                        title={intl.formatMessage({ id: 'modules.priceType' })}
                      >
                        {isComparablesExpanded ? '▼' : '▶'}
                      </ExpandButton>
                    )}
                  </ModuleItemHeader>
                  {module.id === 'valuation' && isValuationExpanded && operation !== undefined && reportType !== undefined && reportType !== 2 && !module.disabled && (
                    <div style={{ 
                      marginTop: '8px', 
                      paddingTop: '8px', 
                      borderTop: '1px solid #e9ecef'
                    }}>
                      <ExcludeOptionsContainer>
                        <ExcludeOptionsLabel>
                          {intl.formatMessage({ id: 'modules.excludeOptions' })}
                        </ExcludeOptionsLabel>
                        {operation === 1 && (
                          <ExcludeCheckboxContainer>
                            <ExcludeCheckboxLabel>
                              <ExcludeCheckbox
                                type="checkbox"
                                checked={localExcludeRent}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                  handleExcludeRentChange(e.target.checked)
                                }
                              />
                              {intl.formatMessage({ id: 'modules.excludeRent' })}
                            </ExcludeCheckboxLabel>
                          </ExcludeCheckboxContainer>
                        )}
                        {operation === 0 && (
                          <ExcludeCheckboxContainer>
                            <ExcludeCheckboxLabel>
                              <ExcludeCheckbox
                                type="checkbox"
                                checked={localExcludeSale}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                  handleExcludeSaleChange(e.target.checked)
                                }
                              />
                              {intl.formatMessage({ id: 'modules.excludeSale' })}
                            </ExcludeCheckboxLabel>
                          </ExcludeCheckboxContainer>
                        )}
                      </ExcludeOptionsContainer>
                    </div>
                  )}
                </ModuleItem>
                {module.id === 'valuation' && isValuationExpanded && operation !== undefined && reportType !== undefined && reportType !== 2 && module.disabled && (
                  <div style={{ 
                    marginTop: '0px', 
                    marginBottom: '8px',
                    padding: '8px',
                    background: '#ffffff',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <ExcludeOptionsContainer>
                      <ExcludeOptionsLabel>
                        {intl.formatMessage({ id: 'modules.excludeOptions' })}
                      </ExcludeOptionsLabel>
                      {operation === 1 && (
                        <ExcludeCheckboxContainer>
                          <ExcludeCheckboxLabel>
                            <ExcludeCheckbox
                              type="checkbox"
                              checked={localExcludeRent}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                handleExcludeRentChange(e.target.checked)
                              }
                            />
                            {intl.formatMessage({ id: 'modules.excludeRent' })}
                          </ExcludeCheckboxLabel>
                        </ExcludeCheckboxContainer>
                      )}
                      {operation === 0 && (
                        <ExcludeCheckboxContainer>
                          <ExcludeCheckboxLabel>
                            <ExcludeCheckbox
                              type="checkbox"
                              checked={localExcludeSale}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                handleExcludeSaleChange(e.target.checked)
                              }
                            />
                            {intl.formatMessage({ id: 'modules.excludeSale' })}
                          </ExcludeCheckboxLabel>
                        </ExcludeCheckboxContainer>
                      )}
                    </ExcludeOptionsContainer>
                  </div>
                )}
                {module.id === 'comparables' && isComparablesExpanded && operation === 1 && !module.disabled && (
                  <div style={{ 
                    marginTop: '8px', 
                    paddingTop: '8px', 
                    borderTop: '1px solid #e9ecef'
                  }}>
                    <ExcludeOptionsContainer>
                      <ExcludeOptionsLabel>
                        {intl.formatMessage({ id: 'modules.priceType' })}
                      </ExcludeOptionsLabel>
                      <PriceSelect
                        value={selectedPriceType}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const newPriceType = e.target.value as 'original' | 'closing';
                          setSelectedPriceType(newPriceType);
                          if (onPriceTypeChange) {
                            onPriceTypeChange(newPriceType);
                          }
                        }}
                      >
                        <option value="original">
                          {intl.formatMessage({ id: 'modules.priceType.original' })}
                        </option>
                        <option value="closing">
                          {intl.formatMessage({ id: 'modules.priceType.closing' })}
                        </option>
                      </PriceSelect>
                      <PriceDescription>
                        <PriceDescriptionTitle>
                          {selectedPriceType === 'closing' 
                            ? intl.formatMessage({ id: 'modules.priceType.closing' })
                            : intl.formatMessage({ id: 'modules.priceType.original' })}
                        </PriceDescriptionTitle>
                        <PriceDescriptionText>
                          {selectedPriceType === 'closing'
                            ? intl.formatMessage({ id: 'modules.priceType.closing.description' })
                            : intl.formatMessage({ id: 'modules.priceType.original.description' })}
                        </PriceDescriptionText>
                        <PriceDescriptionText style={{ marginTop: '8px' }}>
                          {selectedPriceType === 'closing'
                            ? intl.formatMessage({ id: 'modules.priceType.closing.details' })
                            : intl.formatMessage({ id: 'modules.priceType.original.details' })}
                        </PriceDescriptionText>
                      </PriceDescription>
                    </ExcludeOptionsContainer>
                  </div>
                )}
                {module.id === 'comparables' && isComparablesExpanded && operation === 1 && module.disabled && (
                  <div style={{ 
                    marginTop: '0px', 
                    marginBottom: '8px',
                    padding: '8px',
                    background: '#ffffff',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <ExcludeOptionsContainer>
                      <ExcludeOptionsLabel>
                        {intl.formatMessage({ id: 'modules.priceType' })}
                      </ExcludeOptionsLabel>
                      <PriceSelect
                        value={selectedPriceType}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const newPriceType = e.target.value as 'original' | 'closing';
                          setSelectedPriceType(newPriceType);
                          if (onPriceTypeChange) {
                            onPriceTypeChange(newPriceType);
                          }
                        }}
                      >
                        <option value="original">
                          {intl.formatMessage({ id: 'modules.priceType.original' })}
                        </option>
                        <option value="closing">
                          {intl.formatMessage({ id: 'modules.priceType.closing' })}
                        </option>
                      </PriceSelect>
                      <PriceDescription>
                        <PriceDescriptionTitle>
                          {selectedPriceType === 'closing' 
                            ? intl.formatMessage({ id: 'modules.priceType.closing' })
                            : intl.formatMessage({ id: 'modules.priceType.original' })}
                        </PriceDescriptionTitle>
                        <PriceDescriptionText>
                          {selectedPriceType === 'closing'
                            ? intl.formatMessage({ id: 'modules.priceType.closing.description' })
                            : intl.formatMessage({ id: 'modules.priceType.original.description' })}
                        </PriceDescriptionText>
                        <PriceDescriptionText style={{ marginTop: '8px' }}>
                          {selectedPriceType === 'closing'
                            ? intl.formatMessage({ id: 'modules.priceType.closing.details' })
                            : intl.formatMessage({ id: 'modules.priceType.original.details' })}
                        </PriceDescriptionText>
                      </PriceDescription>
                    </ExcludeOptionsContainer>
                  </div>
                )}
              </React.Fragment>
            ))}
        </ToggleContainer>
      )}
      
      {isLanguageSelectorOpen && (
        <LanguageSelectorContainer ref={languageContainerRef}>
          <LanguageSelectorLabel>
            {intl.formatMessage({ id: 'modules.language' })}
          </LanguageSelectorLabel>
          <LanguageSelect
            value={selectedLanguage}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleLanguageChange(e.target.value)}
          >
            <option value="es">
              {intl.formatMessage({ id: 'modules.language.es' })}
            </option>
            <option value="en">
              {intl.formatMessage({ id: 'modules.language.en' })}
            </option>
            <option value="it">
              {intl.formatMessage({ id: 'modules.language.it' })}
            </option>
            <option value="pt">
              {intl.formatMessage({ id: 'modules.language.pt' })}
            </option>
          </LanguageSelect>
        </LanguageSelectorContainer>
      )}
      
    </>
  );
}; 